import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SITE, waGeneral } from "@/config/siteConfig";
import { trackEvent } from "@/lib/analytics";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import { IMAGES } from "@/config/siteConfig";
import { FinalCTA } from "@/components/home/SocialSections";

const CHANNELS = [
  { icon: Phone, label: "Call Us", value: SITE.phoneDisplay, href: `tel:${SITE.phoneRaw}`, testid: "contact-phone-card", event: "phone_click" },
  { icon: MessageCircle, label: "WhatsApp", value: SITE.phoneDisplay, href: waGeneral(), testid: "contact-whatsapp-card", event: "whatsapp_click" },
  { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email-card", event: null },
];

const ContactPage = () => {
  useSEO({
    title: "Contact Us | Plan Your Kashmir Holiday | Book Kashmir",
    description: "Talk to a Kashmir travel expert. Call, WhatsApp or send an enquiry and get a personalised Kashmir holiday quote.",
    path: "/contact-us",
  });

  return (
    <main data-testid="contact-page">
      <PageHero image={IMAGES.pahalgam} overline="We're Here to Help" title="Contact Book Kashmir"
        subtitle="Call, WhatsApp or send the form — a real Kashmir travel expert will get back to you." />
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-6">Talk to a Travel Expert</h2>
            <div className="space-y-4">
              {CHANNELS.map(({ icon: Icon, label, value, href, testid, event }) => (
                <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                  data-testid={testid} onClick={event ? () => trackEvent(event, { location: "contact_page" }) : undefined}
                  className="flex items-center gap-4 rounded-xl bg-white p-5 ring-1 ring-[#0F2F24]/8 shadow-sm transition-shadow hover:shadow-md">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E3EAE4]">
                    <Icon className="h-5 w-5 text-[#0F2F24]" />
                  </span>
                  <span>
                    <span className="block text-xs font-mono uppercase tracking-wider text-slate-500">{label}</span>
                    <span className="block text-base font-semibold text-[#0F2F24]">{value}</span>
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-6 rounded-xl bg-[#E3EAE4]/70 p-5">
              <p className="flex items-start gap-2 text-sm text-slate-700">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C28519]" /> {SITE.address}
              </p>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-lg ring-1 ring-[#0F2F24]/8">
            <h2 className="font-serif text-2xl font-semibold text-[#0F2F24] mb-1">Send an Enquiry</h2>
            <p className="text-sm text-slate-600 mb-6">Tell us about your trip and we'll reply with a personalised quote.</p>
            <EnquiryForm source="contact-page" testIdPrefix="contact-enquiry" />
          </div>
        </div>
      </section>
      <FinalCTA />
    </main>
  );
};

export default ContactPage;
